<template>
  <div>
    <LoadingSpinner :isLoading="loading" />
    <template v-if="!loading">
      <SoNote v-if="!canRaiseFreeText" tone="muted" icon="lock">{{ $t("securone.freetext.customer_only") }}</SoNote>

      <template v-else>
        <div class="grid grid-cols-1 xl:grid-cols-5 gap-6 items-start">
          <!-- the alarm -->
          <div class="xl:col-span-3 bg-white border border-secondary-200 rounded p-5">
            <h2 class="text-xl font-semibold text-secondary-800 mb-4">{{ $t("securone.freetext.heading") }}</h2>

            <label class="so-label">{{ $t("securone.freetext.field_title") }} <span class="text-red-500">*</span></label>
            <input v-model.trim="form.title" type="text" maxlength="120" class="so-input" :placeholder="$t('securone.freetext.title_placeholder')" />
            <p class="so-hint">{{ $t("securone.freetext.title_hint") }}</p>

            <label class="so-label mt-4">{{ $t("securone.freetext.field_message") }}</label>
            <textarea v-model="form.instruction" rows="4" maxlength="2000" class="so-input" :placeholder="$t('securone.freetext.message_placeholder')"></textarea>
            <p class="so-hint">{{ $t("securone.freetext.message_hint", { n: form.instruction.length }) }}</p>

            <label class="so-label mt-4">{{ $t("securone.freetext.field_code") }} <span class="text-red-500">*</span></label>
            <input v-model.trim="form.resolveCode" type="text" maxlength="50" autocomplete="off" class="so-input so-codein font-mono uppercase" placeholder="ABC123" />
            <p class="so-hint">{{ $t("securone.freetext.code_hint") }}</p>

            <div class="mt-5">
              <div class="text-sm font-semibold text-secondary-800 mb-2">{{ $t("securone.freetext.channels") }}</div>
              <div class="flex flex-col gap-1.5 text-sm text-secondary-700">
                <div class="flex items-center gap-2"><span class="so-check so-check--on"><BaseIcon icon="check" /></span>{{ $t("securone.freetext.channel_sms") }}</div>
                <div class="flex items-center gap-2"><span class="so-check so-check--on"><BaseIcon icon="check" /></span>{{ $t("securone.freetext.channel_app") }}</div>
              </div>
              <p class="so-hint">{{ $t("securone.freetext.channels_help") }}</p>
            </div>

            <div class="mt-5">
              <div class="text-sm font-semibold text-secondary-800 mb-2">{{ $t("securone.freetext.scope") }}</div>
              <div class="rounded border border-secondary-200 divide-y divide-secondary-100">
                <label class="flex items-start gap-3 px-3 py-2.5 cursor-pointer" :class="{ 'bg-blue-50': scope === 'customer' }">
                  <input v-model="scope" type="radio" value="customer" class="form-radio mt-0.5 text-securdia-blue" />
                  <span><span class="text-sm font-medium text-secondary-800">{{ $t("securone.freetext.scope_customer") }}</span><br /><span class="text-xs text-secondary-500">{{ $t("securone.freetext.scope_customer_help", { customer: customer ? customer.name : "" }) }}</span></span>
                </label>
                <label class="flex items-start gap-3 px-3 py-2.5 cursor-pointer" :class="{ 'bg-blue-50': scope === 'departments' }">
                  <input v-model="scope" type="radio" value="departments" class="form-radio mt-0.5 text-securdia-blue" />
                  <span><span class="text-sm font-medium text-secondary-800">{{ $t("securone.freetext.scope_departments") }}</span><br /><span class="text-xs text-secondary-500">{{ $t("securone.freetext.scope_departments_help") }}</span></span>
                </label>
                <div v-if="scope === 'departments'" class="px-3 py-2 flex flex-wrap gap-2 bg-secondary-50">
                  <label v-for="d in departments" :key="d._id" class="inline-flex items-center gap-2 text-sm text-secondary-800 cursor-pointer">
                    <input v-model="scopeDepartments" type="checkbox" :value="String(d._id)" class="form-checkbox h-4 w-4 text-securdia-blue" />{{ d.name }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- who receives, what is attached -->
          <div class="xl:col-span-2 flex flex-col gap-4">
            <div class="bg-white border border-secondary-200 rounded p-4">
              <div class="text-sm font-semibold text-secondary-800 mb-2">{{ $t("securone.freetext.groups_title") }}</div>
              <div v-if="visibleGroups.length" class="rounded border border-secondary-200 divide-y divide-secondary-100">
                <label v-for="g in visibleGroups" :key="g._id" class="flex items-center gap-3 px-3 py-2.5 cursor-pointer" :class="{ 'bg-blue-50': selectedGroups.includes(g._id) }">
                  <input v-model="selectedGroups" type="checkbox" :value="g._id" class="form-checkbox h-4 w-4 text-securdia-blue" />
                  <span class="min-w-0 flex-1">
                    <span class="block text-sm font-medium text-secondary-800">{{ g.name }}</span>
                    <span class="block text-xs text-secondary-500 truncate">{{ g.scope === "global" ? $t("securone.freetext.group_global") : g.departmentNames.join(", ") }}</span>
                  </span>
                  <span class="font-mono text-xs text-secondary-500">{{ g.memberCount - g.smsCount }}<template v-if="g.smsCount"> · {{ g.smsCount }} SMS</template></span>
                </label>
              </div>
              <p v-else class="text-sm text-secondary-500">{{ $t("securone.freetext.no_groups") }}</p>
              <p class="so-hint">{{ $t("securone.freetext.groups_help") }}</p>
            </div>

            <div class="bg-white border border-secondary-200 rounded p-4">
              <div class="text-sm font-semibold text-secondary-800 mb-2">{{ $t("securone.freetext.documents_title") }}</div>
              <div v-if="documents.length" class="rounded border border-secondary-200 divide-y divide-secondary-100">
                <label v-for="d in documents" :key="d._id" class="flex items-center gap-3 px-3 py-2.5 cursor-pointer" :class="{ 'bg-blue-50': selectedDocuments.includes(d._id) }">
                  <input v-model="selectedDocuments" type="checkbox" :value="d._id" class="form-checkbox h-4 w-4 text-securdia-blue" />
                  <span class="min-w-0 flex-1">
                    <span class="block text-sm font-medium text-secondary-800">{{ d.title }}</span>
                    <span class="block text-xs text-secondary-500">v{{ d.currentVersion }} · {{ dateStamp(d.updatedAt) }}</span>
                  </span>
                </label>
              </div>
              <p v-else class="text-sm text-secondary-500">{{ $t("securone.common.none") }}</p>
              <p class="so-hint">{{ $t("securone.freetext.documents_help") }}</p>
            </div>
          </div>
        </div>

        <SoNote v-if="error" tone="bad" class="mt-4">{{ error }}</SoNote>

        <!-- footer bar: what goes out, then hold to send -->
        <div class="so-footerbar">
          <div class="text-sm text-secondary-700 flex items-center gap-2 min-w-0">
            <BaseIcon icon="users" class="text-secondary-400" />
            <span v-if="complete" class="truncate">{{ $t("securone.freetext.footer", { groups: selectedGroups.length, people: reach.people }) }}<template v-if="reach.sms"> · {{ $t("securone.broadcast.sms_count", { n: reach.sms }) }}</template></span>
            <span v-else class="truncate text-secondary-500">{{ $t("securone.freetext.footer_incomplete") }}</span>
          </div>
          <div class="flex items-center gap-2">
            <HoldButton ref="hold" tone="success" icon="paper-plane" :label="$t('securone.freetext.send')" :disabled="!complete" :sending="sending" @fire="fire" />
            <router-link :to="{ name: 'securone_overview' }" class="sd-btn sd-btn--secondary">{{ $t("securone.common.cancel") }}</router-link>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import SoNote from "./components/SoNote.vue";
import HoldButton from "./components/HoldButton.vue";
import api from "./securone.api";
import * as notifications from "./securone.notifications";
import { dateStamp, errorMessage } from "./securone.helpers";

// A free-text alarm written on the spot by a customer user: title, message,
// resolve code, the groups that receive it and optional documents. Sent and
// handled exactly like a category broadcast (event, acknowledgements, chat,
// voice, resolve with the code). The server allows it for customer users only.
export default {
  name: "SecurOneFreeTextBroadcast",
  components: { SoNote, HoldButton },
  data() {
    return {
      loading: true,
      groups: [],
      documents: [],
      form: { title: "", instruction: "", resolveCode: "" },
      scope: "customer",
      scopeDepartments: [],
      selectedGroups: [],
      selectedDocuments: [],
      sending: false,
      error: null,
    };
  },
  computed: {
    canRaiseFreeText() {
      return this.$store.getters["securone/canRaiseFreeText"];
    },
    customer() {
      return this.$store.getters["securone/customer"];
    },
    departments() {
      return this.$store.getters["securone/departments"];
    },
    visibleGroups() {
      if (this.scope !== "departments" || !this.scopeDepartments.length) return this.groups;
      // global groups belong to every department; department groups must touch a chosen one
      return this.groups.filter((g) => g.scope === "global" || (g.departmentIds || []).some((id) => this.scopeDepartments.includes(String(id))));
    },
    chosen() {
      return this.groups.filter((g) => this.selectedGroups.includes(g._id));
    },
    reach() {
      return this.chosen.reduce((acc, g) => ({ people: acc.people + (g.memberCount - g.smsCount), sms: acc.sms + g.smsCount }), { people: 0, sms: 0 });
    },
    complete() {
      return !!this.form.title && !!this.form.resolveCode && this.selectedGroups.length > 0;
    },
  },
  methods: {
    dateStamp,
    async load() {
      this.loading = true;
      try {
        const [groups, documents] = await Promise.all([api.groups(), api.documents()]);
        this.groups = groups;
        this.documents = documents.filter((d) => !d.archived);
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      } finally {
        this.loading = false;
      }
    },
    async fire() {
      if (this.sending || !this.complete) return;
      this.sending = true;
      this.error = null;
      try {
        const event = await api.createEvent({
          title: this.form.title,
          instruction: this.form.instruction,
          resolveCode: this.form.resolveCode,
          groupIds: this.selectedGroups,
          documentIds: this.selectedDocuments,
        });
        notifications.markSeen(event._id); // my own alarm must not notify me
        this.$toasted.show(this.$t("securone.freetext.sent_toast", { n: event.recipientCount }), { type: "success", theme: "outline", position: "top-right", duration: 6000 });
        this.$router.push({ name: "securone_event", params: { id: event._id } });
      } catch (error) {
        this.error = errorMessage(this, error, "securone.errors.broadcast_failed");
        this.sending = false;
        if (this.$refs.hold) this.$refs.hold.reset();
      }
    },
  },
  watch: {
    visibleGroups(list) {
      // a group hidden by the scope filter is not sent to
      const ids = list.map((g) => g._id);
      this.selectedGroups = this.selectedGroups.filter((id) => ids.includes(id));
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.freetext.title"), "securone_freetext");
    this.load();
  },
};
</script>
